import React, { useEffect, useRef, useState } from 'react';
import PFCHeader from './PFCHeaderComponent';
import { getBaseUrl } from '../../helpers/addUserWithotPayment';
import '../../assets/css/pfc.css';

const OTP_LENGTH = 4;
const RESEND_SECONDS = 45;

const OtpReason = ({ type, children }) => (
  <div className="pfc-otp-reason">
    <div className="pfc-otp-reason-icon">
      {type === 'lock' && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <rect x="2.5" y="5.2" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="1" />
          <path d="M4 5.2V3.8C4 2.7 4.9 1.8 6 1.8C7.1 1.8 8 2.7 8 3.8V5.2" stroke="currentColor" strokeWidth="1" />
        </svg>
      )}
      {type === 'shield' && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M6 1.2L10 2.8V5.4C10 7.7 8.4 9.8 6 10.8C3.6 9.8 2 7.7 2 5.4V2.8L6 1.2Z" stroke="currentColor" strokeWidth="1" />
          <path d="M4.2 5.9L5.4 7.1L7.9 4.6" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {type === 'user' && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <circle cx="6" cy="3.6" r="2" stroke="currentColor" strokeWidth="1" />
          <path d="M2.5 10.2C2.8 8.2 4 7.2 6 7.2C8 7.2 9.2 8.2 9.5 10.2" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        </svg>
      )}
    </div>
    <span>{children}</span>
  </div>
);

const formatCountdown = seconds => `00:${String(seconds).padStart(2, '0')}`;

const PFCOtpComponent = ({ token, customer, otpDataId: initialOtpDataId, onVerified, onBack }) => {
  const [otpDataId, setOtpDataId] = useState(initialOtpDataId || null);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [resendSeconds, setResendSeconds] = useState(RESEND_SECONDS);
  const [resending, setResending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  const phoneNumber = customer?.phone || '';
  const phoneLabel = phoneNumber ? `+91 ${phoneNumber}` : 'your registered mobile number';

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;

    const timer = setInterval(() => {
      setResendSeconds(seconds => Math.max(seconds - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendSeconds]);

  const sendOtp = async () => {
    setResending(true);
    setError('');

    try {
      const response = await fetch(`${getBaseUrl()}/patientswebapp/affinity/sendAffinityCustomerOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Affinity-Token': token,
        },
        body: JSON.stringify({ phonenumber: phoneNumber }),
      });
      const data = await response.json();

      if (!response.ok || data.isSuccess !== true || !data.otpDataId) {
        throw new Error(data.error || 'Unable to send OTP. Please try again.');
      }

      setOtpDataId(data.otpDataId);
      setOtp(Array(OTP_LENGTH).fill(''));
      setResendSeconds(RESEND_SECONDS);
      inputRefs.current[0]?.focus();
    } catch (sendError) {
      setError(sendError.message || 'Unable to send OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleChange = (index, event) => {
    const value = event.target.value.replace(/\D/g, '').slice(-1);
    const nextOtp = [...otp];
    nextOtp[index] = value;
    setOtp(nextOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (error) setError('');
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = event => {
    event.preventDefault();
    const pastedOtp = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pastedOtp) return;

    const nextOtp = Array(OTP_LENGTH).fill('');
    pastedOtp.split('').forEach((digit, index) => {
      nextOtp[index] = digit;
    });
    setOtp(nextOtp);
    inputRefs.current[Math.min(pastedOtp.length, OTP_LENGTH) - 1]?.focus();
    if (error) setError('');
  };

  const handleVerify = async event => {
    event.preventDefault();
    const otpValue = otp.join('');

    if (otpValue.length !== OTP_LENGTH) {
      setError(`Please enter the ${OTP_LENGTH}-digit OTP.`);
      return;
    }

    if (!otpDataId) {
      setError('OTP session is unavailable. Please resend the OTP.');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      const response = await fetch(`${getBaseUrl()}/patientswebapp/affinity/verifyAffinityCustomerOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Affinity-Token': token,
        },
        body: JSON.stringify({
          phonenumber: phoneNumber,
          otp: otpValue,
          otpDataId,
          name: customer?.name,
          email: customer?.email,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.isSuccess !== true || data.otpVerified !== true) {
        throw new Error(data.error || 'Invalid OTP. Please try again.');
      }

      await onVerified();
    } catch (verifyError) {
      setError(verifyError.message || 'Unable to verify OTP. Please try again.');
      setVerifying(false);
    }
  };

  return (
    <div className="pfc-otp-page">
      <PFCHeader />

      <main className="pfc-otp-content">
        <section className="pfc-otp-intro">
          <div className="pfc-otp-intro-icon">
            <svg width="23" height="23" viewBox="0 0 23 23" fill="none" aria-hidden="true">
              <rect x="5" y="2.5" width="11" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 6.5H13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              <path d="M8 9.5H13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              <path d="M9 14L10.7 15.7L14.5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1>Verify Your Mobile Number</h1>
          <p>We have sent a 4-digit OTP to</p>
          <strong>{phoneLabel}</strong>

          <div className="pfc-otp-why-card">
            <div className="pfc-otp-why-title">Why OTP is required?</div>
            <OtpReason type="lock">Protect your personal information</OtpReason>
            <OtpReason type="shield">Ensure your consent is secure</OtpReason>
            <OtpReason type="user">Verify your identity</OtpReason>
          </div>
        </section>

        <form className="pfc-otp-card" onSubmit={handleVerify}>
          <div className="pfc-otp-card-title">Enter the 4-digit code sent to your phone</div>
          <div className="pfc-otp-inputs" role="group" aria-label="One-time password">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={element => { inputRefs.current[index] = element; }}
                className={`pfc-otp-input ${error ? 'error' : ''}`}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={digit}
                aria-label={`OTP digit ${index + 1}`}
                onChange={event => handleChange(index, event)}
                onKeyDown={event => handleKeyDown(index, event)}
                onPaste={handlePaste}
              />
            ))}
          </div>

          <div className="pfc-otp-resend-timer">
            {resendSeconds > 0 ? (
              <>Resend OTP in <strong>{formatCountdown(resendSeconds)}</strong></>
            ) : (
              'You can request a new OTP'
            )}
          </div>

          {error && <div className="pfc-otp-error" role="alert">{error}</div>}

          <button className="pfc-otp-verify-button" type="submit" disabled={verifying || resending}>
            {verifying ? 'Verifying...' : 'Verify & Continue'}
          </button>

          <div className="pfc-otp-footer-text">
            Didn&apos;t receive it?{' '}
            <button
              className="pfc-otp-resend-button"
              type="button"
              onClick={sendOtp}
              disabled={resendSeconds > 0 || resending || verifying}
            >
              {resending ? 'Sending...' : 'Resend OTP'}
            </button>
          </div>

          {onBack && (
            <button className="pfc-otp-back-button" type="button" onClick={onBack} disabled={verifying || resending}>
              Back
            </button>
          )}
        </form>
      </main>
    </div>
  );
};

export default PFCOtpComponent;
